import * as B from '@blueprintjs/core';
import {useMutation} from '@tanstack/react-query';
import React from 'react';

import Modal from 'app/components/Modal/Modal';
import {api} from 'app/modules/Remote';
import {ApiProjectReport} from 'app/modules/Remote/Feature';

import cs from './styles.module.scss';

const ReportExportDialog: React.FunctionComponent<{
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  reports: ApiProjectReport[];
  email: string;
  onExported: () => void;
}> = ({isOpen, onClose, projectId, reports, email, onExported}) => {
  const exportMutation = useMutation({
    mutationFn: (reportIds: number[]) => api.projectReports(projectId).exportPdfs(reportIds),
    onSuccess: () => {
      onExported();
      onClose();
    },
  });

  // The selection is cleared on success while the dialog is still fading out,
  // so keep showing the reports it was opened with.
  const shownReportsRef = React.useRef(reports);
  if (isOpen) {
    shownReportsRef.current = reports;
  }
  const shownReports = shownReportsRef.current;
  const count = shownReports.length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpening={() => exportMutation.reset()}
      headerText="Export reports as PDF"
      primaryButton={{
        text: 'Export',
        isLoading: exportMutation.isPending,
        isDisabled: count === 0,
        onClick: () => exportMutation.mutate(shownReports.map((r) => r.id)),
      }}
    >
      <p>
        {count === 1 ? 'This report' : `These ${count} reports`} will be exported to PDF in the
        background. We’ll email <strong>{email}</strong> when {count === 1 ? 'it’s' : 'they’re'}{' '}
        ready.
      </p>
      <ul className={cs.exportList}>
        {shownReports.map((report) => (
          <li key={report.id}>
            {report.title}
            <span className={cs.exportListProperty}>{report.feature.displayName}</span>
          </li>
        ))}
      </ul>
      {exportMutation.isError && (
        <B.Callout intent={B.Intent.DANGER}>
          {exportMutation.error?.message ||
            'Something went wrong starting the export. Please try again or contact support.'}
        </B.Callout>
      )}
    </Modal>
  );
};

export default ReportExportDialog;
